import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { getFullName } from '@/lib/utils';
import { Role } from '@/types/auth';
import { UserListItem } from '@/types/user';
import { ArrowUpDown, Edit, FileText, Mail, MoreHorizontal, Trash2 } from 'lucide-react';
import { useState } from 'react';

interface UserTableProps {
  users: UserListItem[];
  isLoading?: boolean;
  /** When set, the actions menu is hidden for the row matching this user (e.g. logged-in user). */
  currentUserEmail?: string;
  /** When set with currentUserEmail, only hide actions for the same user in the same tenant. */
  currentUserTenantId?: string;
  /** When set, admins (role_admin) will not see actions for superadmin users. */
  currentUserRole?: Role;
  onEdit?: (user: UserListItem) => void;
  onDelete?: (user: UserListItem) => void;
  onReinvite?: (user: UserListItem) => void;
  onViewDocuments?: (user: UserListItem) => void;
}

const ROLE_LABELS: Record<Role, string> = {
  role_user: 'User',
  role_admin: 'Admin',
  role_superadmin: 'Super Admin',
};

export function UserTable({
  users,
  isLoading = false,
  currentUserEmail,
  currentUserRole,
  currentUserTenantId,
  onEdit,
  onDelete,
  onReinvite,
  onViewDocuments,
}: UserTableProps) {
  /** Hide actions for: same user, or admin viewing a superadmin. */
  const showActionsFor = (user: UserListItem) => {
    if (currentUserEmail != null && user.email === currentUserEmail && user.tenantId === currentUserTenantId) return false;
    if (currentUserRole === 'role_admin' && user.role === 'role_superadmin') return false;
    return true;
  };
  const [sortField, setSortField] = useState<keyof UserListItem>('createdAt');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  const sortedUsers = [...users].sort((a, b) => {
    const aValue = a[sortField];
    const bValue = b[sortField];

    if (aValue === undefined || bValue === undefined) return 0;

    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return sortDirection === 'asc'
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    }

    if (typeof aValue === 'number' && typeof bValue === 'number') {
      return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
    }

    return 0;
  });

  const handleSort = (field: keyof UserListItem) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getStatusBadgeVariant = (status: UserListItem['status']) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'pending':
        return 'warning';
      case 'inactive':
        return 'secondary';
      default:
        return 'secondary';
    }
  };

  const getRoleBadgeVariant = (role: Role) => {
    switch (role) {
      case 'role_superadmin':
        return 'default';
      case 'role_admin':
        return 'secondary';
      default:
        return 'outline';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const canOpenDetails = (user: UserListItem) =>
    Boolean(onViewDocuments) && user.role === 'role_user';

  const handleRowActivate = (user: UserListItem) => {
    if (canOpenDetails(user)) onViewDocuments?.(user);
  };

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[...Array(5)].map((_, i) => (
          <Skeleton key={i} className="h-16 w-full" />
        ))}
      </div>
    );
  }

  // Mobile Card View
  const MobileUserCard = ({ user }: { user: UserListItem }) => (
    <div
      className={`p-4 border rounded-lg space-y-3 bg-card${canOpenDetails(user) ? ' cursor-pointer' : ''}`}
      onClick={() => handleRowActivate(user)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleRowActivate(user);
        }
      }}
      role={canOpenDetails(user) ? 'button' : undefined}
      tabIndex={canOpenDetails(user) ? 0 : undefined}
    >
      <div className="flex items-start justify-between">
        <div>
          <p className="font-medium">{getFullName(user)}</p>
          <p className="text-sm text-muted-foreground">{user.email}</p>
        </div>
        {showActionsFor(user) && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={(e) => e.stopPropagation()}
              >
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
              <DropdownMenuLabel>Actions</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {onEdit && (
                <DropdownMenuItem onClick={() => onEdit(user)}>
                  <Edit className="mr-2 h-4 w-4" />
                  Edit
                </DropdownMenuItem>
              )}
              {onViewDocuments && user.role === 'role_user' && (
                <DropdownMenuItem onClick={() => onViewDocuments(user)}>
                  <FileText className="mr-2 h-4 w-4" />
                  View Documents
                </DropdownMenuItem>
              )}
              {onReinvite && user.status === 'pending' && (
                <DropdownMenuItem onClick={() => onReinvite(user)}>
                  <Mail className="mr-2 h-4 w-4" />
                  Reinvite
                </DropdownMenuItem>
              )}
              {onDelete && (
                <DropdownMenuItem
                  onClick={() => onDelete(user)}
                  className="text-destructive focus:text-destructive hidden"
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
      <div className="flex flex-wrap gap-2">
        <Badge variant={getRoleBadgeVariant(user.role)}>
          {ROLE_LABELS[user.role]}
        </Badge>
        <Badge variant={getStatusBadgeVariant(user.status)}>
          {user.status}
        </Badge>
      </div>
      <div className="text-sm text-muted-foreground">
        {user.referralCode && <p>Referral: {user.referralCode}</p>}
        <p>Joined: {formatDate(user.createdAt)}</p>
      </div>
    </div>
  );

  return (
    <>
      {/* Mobile View */}
      <div className="md:hidden space-y-3">
        {sortedUsers.map((user) => (
          <MobileUserCard key={user.id} user={user} />
        ))}
      </div>

      {/* Desktop Table */}
      <div className="hidden md:block rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleSort('firstName')}
                  className="-ml-3"
                >
                  Name
                  <ArrowUpDown className="ml-2 h-4 w-4" />
                </Button>
              </TableHead>
              <TableHead>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleSort('email')}
                  className="-ml-3"
                >
                  Email
                  <ArrowUpDown className="ml-2 h-4 w-4" />
                </Button>
              </TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Referral Code</TableHead>
              {/* <TableHead className="text-right">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleSort('documentsCount')}
                  className="-ml-3"
                >
                  Documents
                  <ArrowUpDown className="ml-2 h-4 w-4" />
                </Button>
              </TableHead> */}
              <TableHead>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleSort('createdAt')}
                  className="-ml-3"
                >
                  Joined
                  <ArrowUpDown className="ml-2 h-4 w-4" />
                </Button>
              </TableHead>
              <TableHead className="w-[70px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedUsers.map((user) => (
              <TableRow
                key={user.id}
                className={canOpenDetails(user) ? 'cursor-pointer' : undefined}
                onClick={() => handleRowActivate(user)}
              >
                <TableCell className="font-medium">
                  {getFullName(user)}
                </TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell>
                  <Badge variant={getRoleBadgeVariant(user.role)}>
                    {ROLE_LABELS[user.role]}
                  </Badge>
                </TableCell>
                <TableCell>
                  <Badge variant={getStatusBadgeVariant(user.status)}>
                    {user.status}
                  </Badge>
                </TableCell>
                <TableCell className="text-muted-foreground">
                  {user.referralCode || '—'}
                </TableCell>
                {/* <TableCell className="text-right">{user.documentsCount}</TableCell> */}
                <TableCell>{formatDate(user.createdAt)}</TableCell>
                <TableCell onClick={(e) => e.stopPropagation()}>
                  {showActionsFor(user) && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <MoreHorizontal className="h-4 w-4" />
                          <span className="sr-only">Actions</span>
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuLabel>Actions</DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        {onEdit && (
                          <DropdownMenuItem onClick={() => onEdit(user)}>
                            <Edit className="mr-2 h-4 w-4" />
                            Edit
                          </DropdownMenuItem>
                        )}
                        {onViewDocuments && user.role === 'role_user' && (
                          <DropdownMenuItem onClick={() => onViewDocuments(user)}>
                            <FileText className="mr-2 h-4 w-4" />
                            View Documents
                          </DropdownMenuItem>
                        )}
                        {onReinvite && user.status === 'pending' && (
                          <DropdownMenuItem onClick={() => onReinvite(user)}>
                            <Mail className="mr-2 h-4 w-4" />
                            Reinvite
                          </DropdownMenuItem>
                        )}
                        {onDelete && (
                          <DropdownMenuItem
                            onClick={() => onDelete(user)}
                            className="text-destructive focus:text-destructive hidden"
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            Delete
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </>
  );
}
